import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, subDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Users,
  BarChart2,
  LineChart,
  TrendingUp,
  FileDown,
  Calendar as CalendarIcon,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { supabase } from '@/lib/supabase';
import { useContentPlays } from '@/lib/hooks/use-content-plays';
import { TypesBarChart } from '@/components/charts/types-bar-chart';
import { DonutChart } from '@/components/charts/donut-chart';
import { CONTENT_TYPES, CONTENT_TYPE_COLORS } from '@/lib/constants';
import { ContentType } from '@/types/custom';
import Loading from '@/components/loading';
import { useAdminPreview } from '@/lib/admin-preview-context';

interface OrgOption {
  id: string;
  name: string;
}

interface TeamOption {
  id: string;
  name: string;
  organization_id: string;
}

interface UserOption {
  id: string;
  naam: string | null;
  email: string | null;
}

export default function AdminAnalytics() {
  const { isPreviewMode } = useAdminPreview();
  const analyticsRef = useRef<HTMLDivElement>(null);

  // Fetch hoofdhartuser IDs for preview mode filtering
  const [hnhUserIds, setHnhUserIds] = useState<string[]>([]);
  useEffect(() => {
    if (!isPreviewMode) { setHnhUserIds([]); return; }
    supabase.from('profile_role').select('user_id').eq('role', 'hoofdhartuser')
      .then(({ data }) => { if (data) setHnhUserIds(data.map((r) => r.user_id)); });
  }, [isPreviewMode]);

  // Filter state
  const [selectedOrgId, setSelectedOrgId] = useState<string>('all');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('all');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  // Data from Supabase
  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [filteredUserIds, setFilteredUserIds] = useState<string[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  // New users this period
  const [newUsersCount, setNewUsersCount] = useState(0);

  // Popular keywords
  const [popularKeywords, setPopularKeywords] = useState<
    { keyword: string; count: number }[]
  >([]);

  // Team meditation stats
  const [teamMeditationCount, setTeamMeditationCount] = useState(0);

  // Monthly active users for line chart
  const [monthlyActiveUsers, setMonthlyActiveUsers] = useState<
    { date: string; users: number }[]
  >([]);

  // Fetch organizations and teams on mount
  useEffect(() => {
    async function fetchOrgData() {
      const [{ data: orgs }, { data: teamData }] = await Promise.all([
        supabase.from('organization').select('id, name').order('name'),
        supabase
          .from('team')
          .select('id, name, organization_id')
          .order('name'),
      ]);
      if (orgs) setOrganizations(orgs);
      if (teamData) setTeams(teamData);
    }
    fetchOrgData();
  }, []);

  // Fetch users based on org/team selection
  useEffect(() => {
    async function fetchUsers() {
      setLoadingFilters(true);

      if (selectedTeamId !== 'all') {
        // Users in a specific team
        const { data } = await supabase
          .from('team_user_link')
          .select('user_id, profiles!inner(id, naam, email)')
          .eq('team_id', selectedTeamId);
        if (data) {
          const u = data.map((row: any) => row.profiles);
          setUsers(u);
          setFilteredUserIds(u.map((p) => p.id));
        }
      } else if (selectedOrgId !== 'all') {
        // Users in a specific organization (all teams)
        const { data } = await supabase
          .from('team_user_link')
          .select(
            'user_id, profiles!inner(id, naam, email), team!inner(organization_id)',
          )
          .eq('team.organization_id', selectedOrgId);
        if (data) {
          const u = data.map((row: any) => row.profiles);
          // Deduplicate users who may be in multiple teams
          const unique = Array.from(
            new Map(u.map((p) => [p.id, p])).values(),
          );
          setUsers(unique);
          setFilteredUserIds(unique.map((p) => p.id));
        }
      } else {
        // All users
        let query = supabase
          .from('profiles')
          .select('id, naam, email')
          .order('naam');

        // In preview mode, restrict to hoofdhartuser users
        if (isPreviewMode && hnhUserIds.length > 0) {
          query = query.in('id', hnhUserIds);
        }

        const { data } = await query;
        if (data) {
          setUsers(data);
          setFilteredUserIds(data.map((p) => p.id));
        }
      }

      setLoadingFilters(false);
    }
    fetchUsers();
  }, [selectedOrgId, selectedTeamId, isPreviewMode, hnhUserIds.length]);

  // Get total user count
  useEffect(() => {
    supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .then(({ count }) => {
        if (count !== null) setTotalUserCount(count);
      });
  }, []);

  // Get new users in the date range
  useEffect(() => {
    const userIdsToCheck =
      selectedUserId !== 'all' ? [selectedUserId] : filteredUserIds;
    if (userIdsToCheck.length === 0) {
      setNewUsersCount(0);
      return;
    }

    let query = supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .gte('created_at', dateRange.from.toISOString())
      .lte('created_at', dateRange.to.toISOString());

    if (selectedUserId !== 'all') {
      query = query.eq('id', selectedUserId);
    } else if (selectedOrgId !== 'all' || selectedTeamId !== 'all') {
      query = query.in('id', filteredUserIds);
    }

    query.then(({ count }) => {
      if (count !== null) setNewUsersCount(count);
    });
  }, [filteredUserIds, selectedUserId, dateRange, selectedOrgId, selectedTeamId]);

  // Fetch popular keywords from activity data
  useEffect(() => {
    async function fetchKeywords() {
      // Get content IDs that have been played, then count keywords
      const { data } = await supabase
        .from('activity')
        .select('content_id, content!inner(content_keywords(keywords(name)))')
        .not('content_id', 'is', null)
        .gte('updated_at', dateRange.from.toISOString())
        .lte('updated_at', dateRange.to.toISOString());

      if (!data) return;

      const counts: Record<string, number> = {};
      for (const row of data) {
        const content = row.content as any;
        const ckList = content?.content_keywords || [];
        for (const ck of ckList) {
          const name = ck.keywords?.name;
          if (name) {
            counts[name] = (counts[name] || 0) + 1;
          }
        }
      }

      const sorted = Object.entries(counts)
        .map(([keyword, count]) => ({ keyword, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

      setPopularKeywords(sorted);
    }
    fetchKeywords();
  }, [dateRange]);

  // Fetch monthly active users for the line chart
  useEffect(() => {
    async function fetchMonthlyUsers() {
      let query = supabase
        .from('activity')
        .select('user_id, updated_at')
        .not('content_id', 'is', null)
        .gte('updated_at', dateRange.from.toISOString())
        .lte('updated_at', dateRange.to.toISOString());

      if (selectedUserId !== 'all') {
        query = query.eq('user_id', selectedUserId);
      } else if (filteredUserIds.length > 0 && (selectedOrgId !== 'all' || selectedTeamId !== 'all')) {
        query = query.in('user_id', filteredUserIds);
      }

      const { data } = await query;
      if (!data) return;

      // Group by month, count distinct users
      const monthUsers: Record<string, Set<string>> = {};
      for (const row of data) {
        const d = new Date(row.updated_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!monthUsers[key]) monthUsers[key] = new Set();
        monthUsers[key].add(row.user_id);
      }

      const result = Object.entries(monthUsers)
        .map(([date, userSet]) => ({ date, users: userSet.size }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setMonthlyActiveUsers(result);
    }
    fetchMonthlyUsers();
  }, [dateRange, filteredUserIds, selectedUserId, selectedOrgId, selectedTeamId]);

  // Fetch meditation sessions in the date range
  useEffect(() => {
    async function fetchMeditationCount() {
      const table = isPreviewMode ? 'samen_meditations' : 'team_meditations';

      let query = supabase
        .from(table)
        .select('id', { count: 'exact', head: true })
        .gte('scheduled_at', dateRange.from.toISOString())
        .lte('scheduled_at', dateRange.to.toISOString());

      if (!isPreviewMode) {
        if (selectedTeamId !== 'all') {
          query = query.eq('team_id', selectedTeamId);
        } else if (selectedOrgId !== 'all') {
          const orgTeamIds = teams
            .filter((t) => t.organization_id === selectedOrgId)
            .map((t) => t.id);
          if (orgTeamIds.length > 0) {
            query = query.in('team_id', orgTeamIds);
          }
        }
      }

      if (selectedUserId !== 'all') {
        query = query.eq('created_by', selectedUserId);
      }

      const { count } = await query;
      setTeamMeditationCount(count ?? 0);
    }
    fetchMeditationCount();
  }, [dateRange, selectedOrgId, selectedTeamId, selectedUserId, teams, isPreviewMode]);

  // Build play filters for the hook
  const playFilters = useMemo(() => {
    if (selectedUserId !== 'all') {
      return {
        userId: selectedUserId,
        startTime: dateRange.from,
        endTime: dateRange.to,
      };
    }
    if (filteredUserIds.length > 0) {
      return {
        userIds: filteredUserIds,
        startTime: dateRange.from,
        endTime: dateRange.to,
      };
    }
    return { disabled: true as const };
  }, [filteredUserIds, selectedUserId, dateRange]);

  const { contentPlays, loading: playsLoading } = useContentPlays(playFilters);

  // Compute stats
  const totalPlays = useMemo(
    () =>
      Object.values(contentPlays.types).reduce(
        (sum, t) => sum + (t?.plays || 0),
        0,
      ),
    [contentPlays],
  );

  const displayUserCount =
    selectedUserId !== 'all' ? 1 : filteredUserIds.length;


  // Filtered teams based on selected org
  const filteredTeams =
    selectedOrgId === 'all'
      ? teams
      : teams.filter((t) => t.organization_id === selectedOrgId);

  const loading = loadingFilters || playsLoading;

  // Render bar chart helper
  const renderBarChart = (
    data: any[],
    valueKey: string,
    labelKey: string,
    title: string,
    color = 'bg-blue-500',
  ) => {
    const maxValue = Math.max(...data.map((item) => item[valueKey]), 1);
    return (
      <div className="border rounded-lg p-4 bg-white shadow-sm">
        <h3 className="text-lg font-medium mb-4">{title}</h3>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen data beschikbaar</p>
        ) : (
          <div className="space-y-3">
            {data.map((item, index) => (
              <div key={index} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>{item[labelKey]}</span>
                  <span className="font-medium">{item[valueKey]}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div
                    className={`${color} h-2.5 rounded-full`}
                    style={{
                      width: `${(item[valueKey] / maxValue) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Line chart - active users per month
  const renderLineChart = () => {
    const data = monthlyActiveUsers;
    if (data.length === 0) {
      return (
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <h3 className="text-lg font-medium mb-4">Actieve Gebruikers per Maand</h3>
          <p className="text-sm text-muted-foreground">
            Geen activiteit in deze periode
          </p>
        </div>
      );
    }

    const maxValue = Math.max(...data.map((item) => item.users), 1);
    // Add 10% padding to the top of the chart
    const chartMax = Math.ceil(maxValue * 1.1) || 1;
    const points = data
      .map((item, index) => {
        const x = data.length === 1 ? 50 : (index / (data.length - 1)) * 100;
        const y = 100 - (item.users / chartMax) * 100;
        return `${x},${y}`;
      })
      .join(' ');

    // Fill area under the line
    const areaPoints = `0,100 ${points} 100,100`;

    return (
      <div className="border rounded-lg p-4 bg-white shadow-sm">
        <h3 className="text-lg font-medium mb-4">Actieve Gebruikers per Maand</h3>
        <div className="relative h-[200px] mt-6 mb-8">
          {/* Y-axis label */}
          <div className="absolute -left-1 top-1/2 -translate-y-1/2 -rotate-90 text-xs text-gray-400 whitespace-nowrap">
            Gebruikers
          </div>
          {/* Y-axis values */}
          <div className="absolute left-6 top-0 h-full flex flex-col justify-between text-xs text-gray-500">
            <span>{chartMax}</span>
            <span>{Math.round(chartMax / 2)}</span>
            <span>0</span>
          </div>
          <div className="absolute left-12 right-0 h-full">
            {/* Grid lines */}
            <div className="absolute w-full h-full flex flex-col justify-between">
              <div className="border-b border-gray-100 w-full"></div>
              <div className="border-b border-gray-100 w-full"></div>
              <div className="border-b border-gray-200 w-full"></div>
            </div>
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {/* Area fill */}
              <polygon
                points={areaPoints}
                fill="rgba(59, 130, 246, 0.1)"
              />
              {/* Line */}
              <polyline
                points={points}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            {/* Data points with tooltips (separate SVG for proper scaling) */}
            <svg
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
            >
              {data.map((item, index) => {
                const x =
                  data.length === 1
                    ? 50
                    : (index / (data.length - 1)) * 100;
                const y = 100 - (item.users / chartMax) * 100;
                return (
                  <g key={index}>
                    <circle
                      cx={`${x}%`}
                      cy={`${y}%`}
                      r="4"
                      fill="white"
                      stroke="#3b82f6"
                      strokeWidth="2"
                    />
                    <title>{item.users} gebruikers</title>
                  </g>
                );
              })}
            </svg>
            {/* X-axis labels */}
            <div className="absolute w-full top-full flex justify-between text-xs text-gray-500 mt-2">
              {data.map((item, index) => {
                if (
                  data.length <= 6 ||
                  index % Math.ceil(data.length / 6) === 0 ||
                  index === data.length - 1
                ) {
                  const [year, month] = item.date.split('-');
                  const d = new Date(parseInt(year), parseInt(month) - 1);
                  return (
                    <span key={index}>
                      {d.toLocaleString('nl', { month: 'short', year: '2-digit' })}
                    </span>
                  );
                }
                return <span key={index}></span>;
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // PDF export
  const exportToPDF = async () => {
    if (!analyticsRef.current) return;
    try {
      setPdfLoading(true);
      const canvas = await html2canvas(analyticsRef.current, {
        scale: 1,
        useCORS: true,
        logging: false,
        allowTaint: true,
      });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, imgHeight);
      const date = new Date();
      pdf.save(
        `analytics_${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}.pdf`,
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  // Top content for bar chart
  const topContentData = contentPlays.topPlayed.slice(0, 8).map((item) => ({
    title: item.content.title,
    plays: item.plays,
  }));

  return (
    <div className="space-y-6" ref={analyticsRef}>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-semibold sm:mr-6">{isPreviewMode ? 'HnH Analytics' : 'Analytics'}</h1>

          <div className="flex flex-wrap gap-3 items-end flex-1">
            {/* Organization filter */}
            <div className="w-full sm:w-[200px]">
              <select
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                  setSelectedTeamId('all');
                  setSelectedUserId('all');
                }}
              >
                <option value="all">Alle organisaties</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Team filter */}
            <div className="w-full sm:w-[200px]">
              <select
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={selectedTeamId}
                onChange={(e) => {
                  setSelectedTeamId(e.target.value);
                  setSelectedUserId('all');
                }}
              >
                <option value="all">Alle teams</option>
                {filteredTeams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            {/* User filter */}
            <div className="w-full sm:w-[200px]">
              <select
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="all">Alle gebruikers</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.naam || user.email || user.id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              {loading && (
                <div className="flex items-center text-blue-600">
                  <div className="animate-spin mr-2 h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  <span>Laden...</span>
                </div>
              )}
              <div className="flex items-center gap-1 mr-2 border rounded-md px-2 py-1 bg-white">
                <span className="text-sm text-gray-600 whitespace-nowrap">Periode</span>
                <input
                  type="date"
                  className="text-sm border-0 bg-transparent focus:outline-none focus:ring-0 w-[120px] text-gray-700"
                  value={format(dateRange.from, 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const d = new Date(e.target.value);
                    if (!isNaN(d.getTime())) {
                      setDateRange((prev) => ({
                        from: d,
                        to: d > prev.to ? d : prev.to,
                      }));
                    }
                  }}
                />
                <span className="text-sm text-gray-400">-</span>
                <input
                  type="date"
                  className="text-sm border-0 bg-transparent focus:outline-none focus:ring-0 w-[120px] text-gray-700"
                  value={format(dateRange.to, 'yyyy-MM-dd')}
                  onChange={(e) => {
                    const d = new Date(e.target.value);
                    if (!isNaN(d.getTime())) {
                      setDateRange((prev) => ({
                        from: d < prev.from ? d : prev.from,
                        to: d,
                      }));
                    }
                  }}
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 ml-1"
                    >
                      <CalendarIcon className="h-4 w-4 text-gray-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <div className="flex flex-col">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={dateRange.from}
                        selected={dateRange}
                        onSelect={(range) => {
                          if (range?.from) {
                            setDateRange({
                              from: range.from,
                              to: range.to || range.from,
                            });
                          }
                        }}
                        locale={nl}
                        numberOfMonths={2}
                        classNames={{
                          cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-green-100 [&:has([aria-selected].day-outside)]:bg-green-50 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
                          day_selected:
                            'bg-green-600 text-white hover:bg-green-700 hover:text-white focus:bg-green-700 focus:text-white',
                          day_range_middle:
                            'aria-selected:bg-green-100 aria-selected:text-green-900',
                          day_range_start: 'day-range-start',
                          day_range_end: 'day-range-end',
                          day_outside:
                            'day-outside text-muted-foreground opacity-50 aria-selected:bg-green-50 aria-selected:text-muted-foreground aria-selected:opacity-30',
                        }}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                onClick={exportToPDF}
                disabled={pdfLoading}
              >
                <FileDown className="h-4 w-4" />
                <span>Exporteer PDF</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gebruikers</p>
                <p className="text-2xl font-bold">{displayUserCount}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-green-500 text-sm flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                {newUsersCount}
              </span>
              <span className="text-sm text-gray-500 ml-1">
                nieuw in periode
              </span>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Actieve Gebruikers</p>
                <p className="text-2xl font-bold">
                  {contentPlays.activeUsers}
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-sm text-gray-500">
                {displayUserCount > 0
                  ? Math.round(
                      (contentPlays.activeUsers / displayUserCount) * 100,
                    )
                  : 0}
                % van totaal
              </span>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Totaal Afgespeeld</p>
                <p className="text-2xl font-bold">{totalPlays}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <BarChart2 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-sm text-gray-500">sessies in periode</span>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  Gemiddeld per Gebruiker
                </p>
                <p className="text-2xl font-bold">
                  {displayUserCount > 0
                    ? Math.round(totalPlays / displayUserCount)
                    : 0}
                </p>
              </div>
              <div className="bg-amber-100 p-3 rounded-full">
                <LineChart className="h-6 w-6 text-amber-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-sm text-gray-500">
                sessies per gebruiker
              </span>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Team Meditaties</p>
                <p className="text-2xl font-bold">{teamMeditationCount}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-sm text-gray-500">sessies in periode</span>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {renderLineChart()}
          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <h3 className="text-lg font-medium mb-4">Content Verdeling</h3>
            <DonutChart contentPlays={contentPlays} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <h3 className="text-lg font-medium mb-4">Sessies per Type</h3>
            <TypesBarChart contentPlays={contentPlays} />
          </div>

          {renderBarChart(
            topContentData,
            'plays',
            'title',
            'Meest Afgespeelde Content',
            'bg-purple-500',
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {renderBarChart(
            popularKeywords,
            'count',
            'keyword',
            'Meest Populaire Themas',
            'bg-amber-500',
          )}
        </div>
      </div>
    </div>
  );
}
